<template>
  <span 
    v-if="change !== null && change !== undefined" 
    ref="chipRef"
    :class="['comparison-text', changeType, { 'has-tooltip': hasTooltipContent, 'inverted': props.inverted }]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    {{ formatChange(change) }}
  </span>
  <Teleport to="body">
    <div 
      v-if="hasTooltipContent && showTooltip && tooltipPosition"
      ref="tooltipRef"
      class="tooltip"
      :style="tooltipStyle"
    >
      <table class="tooltip-table">
        <tr v-if="formattedDateRange">
          <td class="tooltip-label">
            Previous period:
          </td>
          <td class="tooltip-value">
            {{ formattedDateRange }}
          </td>
        </tr>
        <tr v-if="props.previousValue !== null && props.previousValue !== undefined && props.previousValue !== ''">
          <td class="tooltip-label">
            {{ metricLabel }}:
          </td>
          <td class="tooltip-value">
            {{ props.previousValue }}
          </td>
        </tr>
      </table>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  change: {
    type: [Number, String, null],
    default: null
  },
  previousValue: {
    type: [String, Number, null],
    default: null
  },
  previousPeriodDates: {
    type: Object,
    default: null
  },
  inverted: {
    type: Boolean,
    default: false
  },
  dateRangePreset: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: null
  },
  tooltipMinWidth: {
    type: [String, Number],
    default: '200'
  }
})

const changeType = computed(() => {
  if (props.change === null || props.change === undefined) return ''
  
  // Parse the change value, handling both string and number types
  let numChange
  if (typeof props.change === 'string') {
    numChange = parseFloat(props.change.replace('%', '').trim())
  } else {
    numChange = Number(props.change)
  }
  
  // Handle NaN case
  if (isNaN(numChange)) return 'neutral'
  
  // If change is 0% (or very close to 0%), return neutral
  if (Math.abs(numChange) < 0.1) return 'neutral'
  
  // For inverted widgets, we keep arrow direction matching change direction
  // but swap colors via CSS. So:
  // - Positive change (increase) = positive class (arrow up) + inverted class (swaps to yellow)
  // - Negative change (decrease) = negative class (arrow down) + inverted class (swaps to green)
  // We don't flip the class here - the CSS will handle color inversion
  
  // Normal logic: positive change = good (green), negative change = bad (yellow)
  // This is what ending soon widgets should use (since they invert the calculation)
  if (numChange > 0) {
    return 'positive' // Green - good
  }
  return 'negative' // Yellow - bad
})

const formatChange = (change) => {
  if (change === null) return ''
  
  // Parse the change value
  let numChange
  if (typeof change === 'string') {
    numChange = parseFloat(change.replace('%', '').replace('+', '').replace('-', '').trim())
  } else {
    numChange = Number(change)
  }
  
  // Handle NaN case
  if (isNaN(numChange)) return '0%'
  
  // Add +, -, or ± sign before the percentage
  const sign = numChange > 0 ? '+' : (numChange < 0 ? '-' : '±')
  const absValue = Math.abs(numChange)
  
  return `${sign}${absValue}%`
}

const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  })
}

const formattedDateRange = computed(() => {
  if (!props.previousPeriodDates) return null
  
  // If date range is 'today', show "Yesterday" instead of date range
  if (props.dateRangePreset === 'today') {
    return 'Yesterday'
  }
  
  const { from_timestamp, to_timestamp } = props.previousPeriodDates
  const fromDate = formatDate(from_timestamp)
  const toDate = formatDate(to_timestamp)
  if (fromDate && toDate) {
    return `${fromDate} - ${toDate}`
  }
  return null
})

const metricLabel = computed(() => {
  return props.label || 'Previous value'
})

const hasTooltipContent = computed(() => {
  return (formattedDateRange.value !== null) || 
         (props.previousValue !== null && props.previousValue !== undefined && props.previousValue !== '')
})

const chipRef = ref(null)
const tooltipRef = ref(null)
const showTooltip = ref(false)
const tooltipPosition = ref(null)

const tooltipStyle = computed(() => {
  if (!tooltipPosition.value) return {}
  const minWidth = typeof props.tooltipMinWidth === 'number' 
    ? `${props.tooltipMinWidth}px` 
    : props.tooltipMinWidth
  return {
    left: `${tooltipPosition.value.left}px`,
    top: `${tooltipPosition.value.top}px`,
    transform: 'translateX(-50%)',
    minWidth: minWidth
  }
})

const updateTooltipPosition = () => {
  if (!chipRef.value || !showTooltip.value) return
  
  const rect = chipRef.value.getBoundingClientRect()
  const tooltipHeight = tooltipRef.value?.offsetHeight || 60 // Estimate if not yet rendered
  const margin = 8
  
  tooltipPosition.value = {
    left: rect.left + rect.width / 2,
    top: rect.top - tooltipHeight - margin
  }
}

const handleMouseEnter = () => {
  if (!hasTooltipContent.value) return
  showTooltip.value = true
  nextTick(() => {
    updateTooltipPosition()
    // Update again after tooltip is rendered to get accurate height
    if (tooltipRef.value) {
      requestAnimationFrame(() => {
        updateTooltipPosition()
      })
    }
  })
}

const handleMouseLeave = () => {
  showTooltip.value = false
  tooltipPosition.value = null
}

const handleScroll = () => {
  if (showTooltip.value) {
    updateTooltipPosition()
  }
}

const handleResize = () => {
  if (showTooltip.value) {
    updateTooltipPosition()
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, true)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.comparison-text {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  margin-left: 8px;
}

.comparison-text.positive {
  color: #448361; /* Green */
}

.comparison-text.negative {
  color: #CB912F; /* Orange */
}

.comparison-text.neutral {
  color: #6b7280;
}

/* When inverted, swap the colors */
.comparison-text.inverted.positive {
  color: #CB912F; /* Orange */
}

.comparison-text.inverted.negative {
  color: #448361; /* Green (was yellow) */
}

.comparison-text {
  position: relative;
  z-index: 1;
}

.comparison-text.has-tooltip {
  cursor: help;
  z-index: 10010;
}

.tooltip {
  position: fixed;
  background-color: #333;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  z-index: 10010;
  pointer-events: none;
  transition: opacity 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.tooltip-table {
  width: 100%;
  border-collapse: collapse;
}

.tooltip-table tr {
  border: none;
}

.tooltip-table td {
  padding: 4px 0;
  border: none;
  vertical-align: top;
}

.tooltip-label {
  text-align: left;
  padding-right: 16px;
  white-space: nowrap;
}

.tooltip-value {
  text-align: right;
  white-space: nowrap;
  font-weight: 600;
}

.comparison-text:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

</style>
